<template>
  <a-loading :loading="integrationMeta.loading">
    <l-rows gap="1">
      <l-columns space="between">
        <a-title :text="this.$t('integrations.pushPageTitle')" />
        <a-button
          variant="primary"
          :disabled="pushDisabled"
          tooltip-position="left"
          :tooltip="pushDisabled ? this.$t('versions.missingPropertiesExist') : ''"
          :text="this.$t('integrations.pushPageTitle')"
          @click="handlePush"
        />
      </l-columns>
      <l-columns class="full-height-columns">
        <a-box>
          <a-title
            size="4"
            :text="integration.name"
          />
          <dl class="cf-push-configuration__options">
            <div>
              <h5>{{ $t('integrations.settings') }}</h5>
              <div class="cf-push-configuration__option cf-push-configuration__option--key-value">
                <dt>{{ $t('integrations.gameRoundValidityPeriod') }}</dt>
                <dd>{{ integration.gameRoundValidityPeriod == null ? '-' : integration.gameRoundValidityPeriod }}</dd>
              </div>
              <div class="cf-push-configuration__option cf-push-configuration__option--key-value">
                <dt>{{ $t('integrations.defaultCasinoId') }}</dt>
                <dd>{{ defaultCasinoId == null ? '-' : defaultCasinoId }}</dd>
              </div>
            </div>
            <div>
              <h5>{{ $t('integrations.bridgeInfo') }}</h5>
              <div class="cf-push-configuration__option cf-push-configuration__option--key-value">
                <dt>{{ $t('common.name') }}</dt>
                <dd>{{ integration.bridge.name == null ? '-' : integration.bridge.name }}</dd>
              </div>
              <div class="cf-push-configuration__option cf-push-configuration__option--key-value">
                <dt>{{ $t('bridges.routerUrl') }}</dt>
                <dd>{{ integration.bridge.routerUrl == null ? '-' : integration.bridge.routerUrl }}</dd>
              </div>
            </div>
          </dl>
        </a-box>
      </l-columns>
      <a-box>
        <back-office-list
          class="has-word-break-all"
          :list-data="casinos"
          :columns="casinoColumns"
          :selectable="false"
        />
      </a-box>
      <a-box>
        <back-office-list
          class="has-word-break-all"
          :list-data="integrationConfigurationWithValidation"
          :columns="columns"
          :selectable="false"
        />
      </a-box>
    </l-rows>
  </a-loading>
</template>

<script>
  import { useIntegration } from '@/views/integrations/integrationHooks'
  import integrationService from '@/services/integrationService'
  import { IntegrationGameActiveBadge, HighlightedUrl, IconColumn } from '@/components'
  import BackOfficeList from '@/components/BackOfficeList'
  import { ABox, AButton, ALoading, ATitle, LColumns, LRows } from 'codefabrik-gui-vue'
  import { toast } from '@/shared'

  export default {
    name: 'PushIntegration',
    components: {
      ALoading,
      BackOfficeList,
      AButton,
      ATitle,
      LRows,
      LColumns,
      ABox
    },
    data () {
      return {
        integration: {},
        casinos: [],
        defaultCasinoId: null,
        integrationConfigurationWithValidation: [],
        pushDisabled: false
      }
    },
    computed: {
      columns () {
        return [
          {
            size: '1',
            component: IconColumn,
            componentProperties: {
              icon: (item) => {return this.resolveIcon(item)},
            },
            header: this.$t('common.valid'),
            contentPosition: 'center'
          },
          {
            header: this.$t('common.game'),
            field: 'gameName',
          },
          {
            header: this.$t('common.version'),
            field: 'gameVersion',
          },
          {
            header: this.$t('common.status'),
            component: IntegrationGameActiveBadge,
            componentProperties: {
              integrationGame: it => it
            }
          },
          {
            header: this.$t('versions.frontendUrl'),
            field: 'frontendUrl',
            component: HighlightedUrl,
            componentProperties: {
              item: (item) => item,
              field: 'frontendUrl'
            },
          },
          {
            header: this.$t('versions.backendUrl'),
            field: 'backendUrl',
            component: HighlightedUrl,
            componentProperties: {
              item: (item) => item,
              field: 'backendUrl'
            },
          },
          {
            header: this.$t('versions.historyUrl'),
            field: 'historyUrl',
            component: HighlightedUrl,
            componentProperties: {
              item: (item) => item,
              field: 'historyUrl'
            },
          },
        ]
      },
      casinoColumns () {
        return [
          {
            header: this.$t('common.name'),
            field: 'casinoName',
          },
          {
            header: this.$t('casinos.id'),
            field: 'casinoId',
          },
          {
            header: this.$t('casinos.jurisdictionKey'),
            field: 'jurisdictionKey',
          },
          {
            header: this.$t('casinos.maxRTP'),
            field: 'maxRTP',
          },
          {
            header: this.$t('casinos.showDevelopStartPage'),
            field: 'options.showDevelopStartPage',
          },
          {
            header: this.$t('casinos.sendEventMessages'),
            field: 'options.sendEventMessages',
          },
        ]
      }
    },
    mixins: [
      useIntegration('integration')
    ],
    methods: {
      getTooltip (item) {
        let tooltip = ''
        item.unreplacedFrontendProperties.unreplacedProperties.forEach((element, index) => {
          if (index === 0) {
            tooltip += this.$t('versions.frontendUrl') + ': ' + item.frontendUrl
            tooltip += '\n' + this.$t('versions.missingProperties') + ':\n'
          }
          tooltip += element + '\n'
        })

        if (tooltip !== '') {
          tooltip += '\n'
        }

        item.unreplacedBackendProperties.unreplacedProperties.forEach((element, index) => {
          if (index === 0) {
            tooltip += this.$t('versions.backendUrl') + ': ' + item.backendUrl
            tooltip += '\n' + this.$t('versions.missingProperties') + ':\n'
          }
          tooltip += element + '\n'
        })
        return tooltip
      },
      resolveIcon (item) {
        if (item.unreplacedBackendProperties.unreplacedProperties.length > 0
          || item.unreplacedFrontendProperties.unreplacedProperties.length > 0) {
          this.pushDisabled = true
          return {
            danger: true,
            tooltip: this.getTooltip(item),
            icon: 'fas fa-exclamation-triangle'
          }
        } else {
          return {
            danger: false,
            icon: 'fas fa-check'
          }
        }
      },
      getIntegrationConfig () {
        integrationService.getIntegrationConfiguration(this.$route.params.id).then((response) => {
          this.integrationConfigurationWithValidation = response.properties.sort((a, b) => a.gameName.localeCompare(b.gameName))
          this.casinos = response.casinos
          this.defaultCasinoId = response.defaultCasinoId;
        })
      },
      handlePush () {
        integrationService.pushIntegrationConfiguration(this.$route.params.id)
          .then(() => {
            toast.showSuccessToast(this.$t('integrations.pushSuccessful'))
          })
          .catch((serverError) => {
            let errorMessage = this.$t('integrations.pushFailed')
            if (serverError !== '') {
              errorMessage += ': ' + serverError
            }
            toast.showErrorToast(errorMessage)
          })
      }
    },
    mounted () {
      this.getIntegrationConfig()
    }
  }
</script>

<style
  lang="scss"
  scoped
>
  @import "../../assets/styles";

  .has-word-break-all {
    word-break: break-all;
  }

  .full-height-columns > .cf-columns__column > * {
    height: 100%;
  }

  h5 {
    font-weight: bold;
  }

  .cf-push-configuration__option {
    dt {
      color: #676767;
    }
  }

  .cf-push-configuration__option--key-value {
    display: grid;
    grid-gap: 0.5rem;
    grid-template-columns: 1fr 1fr;
  }

  .cf-push-configuration__options > div {
    margin-top: 0.5rem;
  }

  @include widescreen {
    .cf-push-configuration__options {
      display: grid;
      grid-gap: 1rem;
      grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    }
  }
</style>
